import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { CalendarDays, CalendarX, Download, Loader2, RefreshCw, Shield, Video } from 'lucide-react';
import SlotPicker, { Slot, browserTimezone } from './SlotPicker';
import {
  MeetingSummary,
  PublicLinkInfo,
  PublicShell,
  StatusCard,
  errorMessage,
  formatSlotRange,
  icsHref,
  pickText
} from './AgendamentoPublic';
import { Button } from '../../components/UI';

type LocaleCode = 'pt' | 'en' | 'es';

interface ManageData {
  reserva: { firstName: string; lastName: string; email: string; startAt: string; endAt: string; status: 'confirmed' | 'cancelled'; lang?: LocaleCode };
  link: PublicLinkInfo & { token: string | null };
  meetingUrl: string;
  canChange: boolean;
  canReschedule: boolean;
  googleCalUrl?: string;
}

const AgendamentoGerenciar: React.FC = () => {
  const { manageToken } = useParams<{ manageToken: string }>();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.split('-')[0] || 'pt') as LocaleCode;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ManageData | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [mode, setMode] = useState<'view' | 'confirmCancel' | 'reschedule'>('view');
  const [newSlot, setNewSlot] = useState<Slot | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');
  const [pickerKey, setPickerKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`/agendamento/public/reserva/${manageToken}`);
      setData(res.data);
    } catch (err) {
      setErrorMsg(errorMessage(err, t('agendamento.reservaNotFound')));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manageToken]);

  useEffect(() => {
    load();
  }, [load]);

  // Abre no idioma em que o cliente reservou, salvo escolha explícita no site
  useEffect(() => {
    if (!data?.reserva.lang) return;
    let userChoseLang = false;
    try {
      userChoseLang = !!localStorage.getItem('neia_lang');
    } catch {
      /* storage indisponível */
    }
    if (!userChoseLang) i18n.changeLanguage(data.reserva.lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.reserva.lang]);

  const handleCancel = async () => {
    setBusy(true);
    setActionError('');
    try {
      await axios.post(`/agendamento/public/reserva/${manageToken}/cancelar`);
      setNotice(t('agendamento.cancelDone'));
      setMode('view');
      await load();
    } catch (err) {
      setActionError(errorMessage(err, t('agendamento.submitError')));
    } finally {
      setBusy(false);
    }
  };

  const handleReschedule = async () => {
    if (!newSlot) return;
    setBusy(true);
    setActionError('');
    try {
      const res = await axios.post(`/agendamento/public/reserva/${manageToken}/remarcar`, {
        startAt: newSlot.startAt,
        clientTimezone: browserTimezone()
      });
      // A reserva remarcada tem um novo token de gestão: troca a URL sem recarregar a página
      const nextToken: string | undefined = res.data.manageToken;
      setNotice(t('agendamento.rescheduleDone'));
      setMode('view');
      setNewSlot(null);
      if (nextToken) {
        window.location.replace(`${window.location.pathname.replace(/[^/]+$/, nextToken)}?ok=1`);
        return;
      }
      await load();
    } catch (err) {
      setActionError(errorMessage(err, t('agendamento.submitError')));
      setNewSlot(null);
      setPickerKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('ok')) setNotice(t('agendamento.rescheduleDone'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" aria-label={t('common.loading') as string} />
      </div>
    );
  }

  if (!data) {
    return (
      <StatusCard icon={<Shield className="w-12 h-12 text-amber-500" />} title={t('agendamento.linkUnavailable')}>
        <p className="text-gray-600">{errorMsg}</p>
      </StatusCard>
    );
  }

  const { reserva, link } = data;
  const cancelled = reserva.status === 'cancelled';
  const title = pickText(link, lang, 'publicTitle');

  return (
    <PublicShell>
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden lg:grid lg:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="p-5 sm:p-6 border-b border-gray-100 lg:border-b-0 lg:border-r">
          <MeetingSummary info={{ ...link, meetingUrl: !!data.meetingUrl }} title={title} />
        </aside>

        <section className="p-5 sm:p-6 min-w-0">
          <p className="text-sm text-gray-500">{t('agendamento.manageGreeting', { nome: reserva.firstName })}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">{t('agendamento.manageTitle')}</h2>
            <span
              className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                cancelled ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-800'
              }`}
            >
              {cancelled ? t('agendamento.statusCancelled') : t('agendamento.statusConfirmed')}
            </span>
          </div>

          {notice && <p className="mt-4 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</p>}

          <div className={`mt-4 rounded-xl border p-4 ${cancelled ? 'border-gray-200 bg-gray-50' : 'border-primary-200 bg-primary-50/40'}`}>
            <p className={`flex items-start gap-2 font-medium ${cancelled ? 'text-gray-500 line-through' : 'text-primary-900'}`}>
              <CalendarDays className="w-4 h-4 shrink-0 mt-1" />
              <span className="first-letter:uppercase">{formatSlotRange(reserva.startAt, reserva.endAt, i18n.language)}</span>
            </p>
            {!cancelled && data.meetingUrl && (
              <p className="mt-2 text-sm text-gray-600 break-all">
                {t('agendamento.meetingLink')}:{' '}
                <a href={data.meetingUrl} target="_blank" rel="noopener noreferrer" className="text-primary-700 hover:underline">
                  {data.meetingUrl}
                </a>
              </p>
            )}
          </div>

          {cancelled ? (
            <div className="mt-5">
              <p className="text-sm text-gray-600">{t('agendamento.cancelledMsg')}</p>
              {link.token && (
                <Button className="mt-4"
                  to={`/agendar/${link.token}`}>
                  <CalendarDays className="w-4 h-4 shrink-0" />
                  {t('agendamento.rebook')}
                </Button>
              )}
            </div>
          ) : mode === 'reschedule' && link.token ? (
            <div className="mt-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">{t('agendamento.rescheduleTitle')}</h3>
              {actionError && <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{actionError}</p>}
              <SlotPicker
                key={pickerKey}
                token={link.token}
                expiresAt={link.expiresAt}
                selected={newSlot}
                onSelect={setNewSlot}
                excludeStartAt={reserva.startAt}
              />
              {newSlot && (
                <p className="mt-5 text-sm text-primary-700 font-medium first-letter:uppercase">
                  {formatSlotRange(newSlot.startAt, newSlot.endAt, i18n.language)}
                </p>
              )}
              <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2">
                <Button variant="ghost"
                  type="button"
                  onClick={() => {
                    setMode('view');
                    setNewSlot(null);
                    setActionError('');
                  }}>
                  {t('agendamento.back')}
                </Button>
                <Button
                  type="button"
                  onClick={handleReschedule}
                  disabled={!newSlot || busy}>
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t('agendamento.rescheduleSubmit')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-6">
              {!data.canChange && <p className="text-sm text-gray-600">{t('agendamento.pastMeeting')}</p>}
              {data.canChange && !data.canReschedule && <p className="text-sm text-gray-600 mb-4">{t('agendamento.rescheduleClosed')}</p>}

              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                {data.meetingUrl && (
                  <Button className="whitespace-nowrap"
                    href={data.meetingUrl}
                    target="_blank"
                    rel="noopener noreferrer">
                    <Video className="w-4 h-4 shrink-0" />
                    {t('agendamento.joinMeeting')}
                  </Button>
                )}
                {data.googleCalUrl && (
                  <Button variant="outline" className="whitespace-nowrap"
                    href={data.googleCalUrl}
                    target="_blank"
                    rel="noopener noreferrer">
                    <CalendarDays className="w-4 h-4 shrink-0" />
                    {t('agendamento.addGoogle')}
                  </Button>
                )}
                <Button variant="ghost" className="whitespace-nowrap"
                  href={icsHref(manageToken!)}>
                  <Download className="w-4 h-4 shrink-0" />
                  {t('agendamento.downloadIcs')}
                </Button>
              </div>

              {data.canChange && (
                <div className="mt-6 pt-5 border-t border-gray-100">
                  {mode === 'confirmCancel' ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                      <p className="text-sm text-red-800">{t('agendamento.cancelConfirm')}</p>
                      {actionError && <p className="mt-2 text-sm text-red-700">{actionError}</p>}
                      <div className="mt-3 flex flex-col-reverse sm:flex-row gap-2">
                        <Button variant="ghost" size="sm"
                          type="button"
                          onClick={() => setMode('view')}>
                          {t('agendamento.cancelNo')}
                        </Button>
                        <Button variant="danger" size="sm"
                          type="button"
                          onClick={handleCancel}
                          disabled={busy}>
                          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                          {t('agendamento.cancelYes')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-2">
                      {data.canReschedule && (
                        <Button variant="ghost" size="sm"
                          type="button"
                          onClick={() => {
                            setMode('reschedule');
                            setNotice('');
                          }}>
                          <RefreshCw className="w-4 h-4" />
                          {t('agendamento.rescheduleBtn')}
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="text-red-700 hover:bg-red-50 border-red-200"
                        type="button"
                        onClick={() => {
                          setMode('confirmCancel');
                          setNotice('');
                        }}>
                        <CalendarX className="w-4 h-4" />
                        {t('agendamento.cancelBtn')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </PublicShell>
  );
};

export default AgendamentoGerenciar;
